import type { AdminV2ProviderExecutionRequest } from '@/admin-v2/runtime/tool-execution';
import { workspaceAccessConfig } from '@/config/workspace-access';
import { requiresWorkspaceLoginToGenerate } from '@/config/workspace-login-policy';
import type { AdminV2ToolRuntimePackage } from './tool-types';

export const ADMIN_V2_TOOL_ACCESS_REQUIRED_CODE =
  'ADMIN_V2_TOOL_ACCESS_REQUIRED';

type AccessPolicyRule = NonNullable<
  NonNullable<AdminV2ToolRuntimePackage['accessPolicy']['tool']>
>;

export type AdminV2AccessActor = {
  isAuthenticated: boolean;
  hasPaidAccess: boolean;
  planIds: string[];
  roleIds: string[];
  /** Server-only scope bound to a paid visitor task, never a browser field. */
  oneTimeTaskPaid?: boolean;
  /** Server-only marker for a full administrator's recorded test run. */
  adminTestExempt?: boolean;
};

export type AdminV2AccessViolation = {
  scope: 'tool' | 'field' | 'option' | 'branch';
  requiredAccess: AccessPolicyRule['requiredAccess'];
  fieldId?: string;
  value?: string | number | boolean;
  nodeId?: string;
};

function requireAccessPolicyRule(
  rule: AccessPolicyRule | undefined,
  path: string
): AccessPolicyRule {
  if (!rule) {
    throw new Error(
      `Admin v2 Tool runtime is missing accessPolicy at ${path}.`
    );
  }
  return rule;
}

function evaluateRule(params: {
  rule: AccessPolicyRule;
  actor: AdminV2AccessActor;
}) {
  const { rule } = params;
  if (
    !workspaceAccessConfig.paidEntitlementsEnabled &&
    (rule.requiredAccess === 'paid' || rule.requiredAccess === 'plan')
  )
    return true;
  if (
    params.actor.adminTestExempt === true &&
    (rule.requiredAccess === 'paid' || rule.requiredAccess === 'plan')
  ) {
    return true;
  }
  if (rule.requiredAccess === 'free') return true;
  if (rule.requiredAccess === 'authenticated') {
    return params.actor.isAuthenticated;
  }
  if (rule.requiredAccess === 'paid') {
    return params.actor.hasPaidAccess;
  }
  if (rule.requiredAccess === 'plan') {
    return rule.planIds.some((planId) => params.actor.planIds.includes(planId));
  }
  return rule.roleIds.some((roleId) => params.actor.roleIds.includes(roleId));
}

function hasSubmittedValue(value: unknown) {
  return !(
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function activatesFieldAccessPolicy(params: {
  field: AdminV2ToolRuntimePackage['toolFields'][number];
  value: unknown;
}) {
  if (params.field.control === 'toggle') {
    return params.value === true;
  }
  return hasSubmittedValue(params.value);
}

function selectedOptionMatches(params: {
  submittedValue: unknown;
  optionValue: string | number | boolean;
}) {
  if (Array.isArray(params.submittedValue)) {
    return params.submittedValue.some((item) =>
      Object.is(item, params.optionValue)
    );
  }
  return Object.is(params.submittedValue, params.optionValue);
}

export function evaluateAdminV2ToolAccess(params: {
  runtimePackage: AdminV2ToolRuntimePackage;
  normalizedInput: Record<string, unknown>;
  actor: AdminV2AccessActor;
  providerRequests?: AdminV2ProviderExecutionRequest[];
}): AdminV2AccessViolation | null {
  if (
    requiresWorkspaceLoginToGenerate(params.runtimePackage.toolId) &&
    !params.actor.isAuthenticated &&
    params.actor.oneTimeTaskPaid !== true
  )
    return { scope: 'tool', requiredAccess: 'authenticated' };
  const toolRule = requireAccessPolicyRule(
    params.runtimePackage.accessPolicy?.tool,
    'accessPolicy.tool'
  );
  if (!evaluateRule({ rule: toolRule, actor: params.actor })) {
    return {
      scope: 'tool',
      requiredAccess: toolRule.requiredAccess,
    };
  }

  for (const [
    fieldIndex,
    field,
  ] of params.runtimePackage.toolFields.entries()) {
    const value = params.normalizedInput[field.id];
    const fieldPolicy = requireAccessPolicyRule(
      field.accessPolicy,
      `toolFields[${fieldIndex}].accessPolicy`
    );
    if (
      activatesFieldAccessPolicy({ field, value }) &&
      !evaluateRule({ rule: fieldPolicy, actor: params.actor })
    ) {
      return {
        scope: 'field',
        fieldId: field.id,
        requiredAccess: fieldPolicy.requiredAccess,
      };
    }

    for (const [optionIndex, option] of (field.options ?? []).entries()) {
      const optionPolicy = requireAccessPolicyRule(
        option.accessPolicy,
        `toolFields[${fieldIndex}].options[${optionIndex}].accessPolicy`
      );
      if (
        selectedOptionMatches({
          submittedValue: value,
          optionValue: option.value,
        }) &&
        !evaluateRule({ rule: optionPolicy, actor: params.actor })
      ) {
        return {
          scope: 'option',
          fieldId: field.id,
          value: option.value,
          requiredAccess: optionPolicy.requiredAccess,
        };
      }
    }
  }

  const reachedNodeIds = new Set(
    (params.providerRequests ?? []).flatMap((request) => [
      ...(request.nodeAncestry ?? []),
      ...(request.nodeId ? [request.nodeId] : []),
    ])
  );
  for (const branchPolicy of params.runtimePackage.accessPolicy
    .branchPolicies ?? []) {
    const rule = requireAccessPolicyRule(
      branchPolicy.policy,
      `accessPolicy.branchPolicies.${branchPolicy.nodeId}`
    );
    if (
      reachedNodeIds.has(branchPolicy.nodeId) &&
      !evaluateRule({ rule, actor: params.actor })
    ) {
      return {
        scope: 'branch',
        nodeId: branchPolicy.nodeId,
        requiredAccess: rule.requiredAccess,
      };
    }
  }

  return null;
}
